import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";

/** Encapsulates the Dashboard's "Export found products" (bulk PDF/Excel) async job polling. */
export function useDashboardExport(filters) {
  const { toast } = useToast();
  const [exportingProducts, setExportingProducts] = useState(null);

  const handleExportProducts = async (format) => {
    setExportingProducts(format);
    toast({ title: "Generating quotation...", description: "This may take a minute or two for larger result sets." });
    try {
      const params = { format };
      if (filters.search) params.q = filters.search;
      if (filters.category) params.category = filters.category;
      if (filters.cbmMin !== null) params.cbm_min = filters.cbmMin;
      if (filters.cbmMax !== null) params.cbm_max = filters.cbmMax;
      if (filters.qtyMin !== null) params.qty_min = filters.qtyMin;
      if (filters.qtyMax !== null) params.qty_max = filters.qtyMax;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (filters.sortField && filters.sortOrder) {
        params.sort_field = filters.sortField;
        params.sort_order = filters.sortOrder;
      }
      if (filters.view === "best_selling") params.best_selling = 1;
      if (filters.showAll) params.show_all = 1;
      if (filters.tag) params.tag = filters.tag;
      if (filters.coefficient && filters.coefficient !== 1) params.coefficient = filters.coefficient;
      const { data: jobData } = await api.post("/fm/products/export-jobs", null, { params });
      const jobId = jobData.job_id;

      let status = "pending";
      for (let i = 0; i < 100; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: statusData } = await api.get(`/fm/products/export-jobs/${jobId}`);
        status = statusData.status;
        if (status === "done") {
          const response = await api.get(`/fm/products/export-jobs/${jobId}/download`, { responseType: "blob" });
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", format === "pdf" ? "products_quotation.pdf" : "products_quotation.xlsx");
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast({ title: "Quotation ready", description: "Download started." });
          break;
        }
        if (status === "error") {
          toast({ title: "Export failed", description: statusData.error, variant: "destructive" });
          break;
        }
      }
      if (status === "pending") {
        toast({ title: "Export timed out", description: "Please try narrowing your filters.", variant: "destructive" });
      }
    } catch (e) {
      toast({
        title: "Export failed",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setExportingProducts(null);
    }
  };

  return { exportingProducts, handleExportProducts };
}
